/**
 * web/ Supabase Browser Client (Issue #447 Phase G)
 *
 * F7 SPA에서 사용하는 SupabaseClient. `@tldr/core`의 `getSupabaseClient` singleton을
 * 호출하여 app/ 워크스페이스가 alias로 재사용될 때에도 동일 인스턴스를 공유한다.
 *
 * 환경변수: vite.config.ts에서 `envPrefix: ['VITE_', 'NEXT_PUBLIC_']` 매핑하여
 * 기존 `NEXT_PUBLIC_SUPABASE_URL` / `NEXT_PUBLIC_SUPABASE_ANON_KEY` 를 그대로 사용한다.
 *
 * PKCE flow + detectSessionInUrl=false 옵션은 `@tldr/core/supabase-client`에서 통일.
 * /auth/callback 라우트가 명시적으로 `exchangeCodeForSession`을 호출한다.
 */

import { getSupabaseClient } from "@tldr/core";
import type { SupabaseClient } from "@supabase/supabase-js";

// import.meta.env 타입 선언 (vite 빌드 시 환경변수 inline)
interface ImportMetaEnvLike {
  readonly NEXT_PUBLIC_SUPABASE_URL?: string;
  readonly NEXT_PUBLIC_SUPABASE_ANON_KEY?: string;
  readonly VITE_SUPABASE_URL?: string;
  readonly VITE_SUPABASE_ANON_KEY?: string;
}

function readEnv(): { url: string; key: string } {
  const env = (import.meta as { env: ImportMetaEnvLike }).env;
  const url = env.VITE_SUPABASE_URL ?? env.NEXT_PUBLIC_SUPABASE_URL;
  const key = env.VITE_SUPABASE_ANON_KEY ?? env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

  if (!url || !key) {
    throw new Error(
      "Supabase env가 누락되었습니다. NEXT_PUBLIC_SUPABASE_URL / NEXT_PUBLIC_SUPABASE_ANON_KEY 또는 VITE_* 변형을 설정하세요.",
    );
  }
  return { url, key };
}

const { url, key } = readEnv();

/** Supabase 클라이언트 singleton (web 환경). */
export const supabase: SupabaseClient = getSupabaseClient({
  url,
  key,
  platform: "web",
});
